import TextInput from "@/components/shared/forms/TextInput";
import TooltipTrigger from "@/components/shared/TooltipTrigger";
import { formatDateOnly } from "@/utils/dates";

export type EditableAttendanceField =
  | "DaysExpected"
  | "DaysAttended"
  | "DaysAbsentExcused"
  | "DaysAbsentUnexcused"
  | "DaysAbsentSuspension"
  | "DaysAttendedSuspension";

export type BulkAttendanceRow = {
  CSPPID: string;
  LocalChildId: string;
  ChildName: string;
  EnrollmentStartDate: string;
  EnrollmentEndDate: string;
  fullMonth: boolean;
  hasExistingRecord: boolean;
  DaysExpected: string;
  DaysAttended: string;
  DaysAbsentExcused: string;
  DaysAbsentUnexcused: string;
  DaysAbsentSuspension: string;
  DaysAttendedSuspension: string;
};

export type BulkAttendanceRowErrors = Partial<
  Record<EditableAttendanceField, string>
>;

const EDITABLE_COLUMNS: {
  field: EditableAttendanceField;
  label: string;
  isRequired?: boolean;
}[] = [
  { field: "DaysExpected", label: "Days Expected", isRequired: true },
  { field: "DaysAttended", label: "Days Attended", isRequired: true },
  { field: "DaysAbsentExcused", label: "Absent Excused" },
  { field: "DaysAbsentUnexcused", label: "Absent Unexcused" },
  { field: "DaysAbsentSuspension", label: "Absent Suspension" },
  { field: "DaysAttendedSuspension", label: "Attended Suspension" },
];

export default function BulkAttendanceGrid({
  rows,
  rowErrors,
  onCellChange,
  disabled,
}: {
  rows: BulkAttendanceRow[];
  rowErrors: Record<string, BulkAttendanceRowErrors>;
  onCellChange: (
    csppid: string,
    field: EditableAttendanceField,
    value: string
  ) => void;
  disabled?: boolean;
}) {
  if (rows.length === 0) {
    return (
      <p className="text-muted mb-0">
        No enrolled children found for this Program Year, Preschool, and Month.
      </p>
    );
  }

  return (
    <div style={{ overflowX: "auto", overflowY: "auto", maxHeight: "65vh" }}>
      <table className="bulk-attendance-grid">
        <colgroup>
          <col style={{ width: "18%" }} />
          <col style={{ width: "12%" }} />
          <col style={{ width: "11%" }} />
          <col style={{ width: "11%" }} />
          {EDITABLE_COLUMNS.map((c) => (
            <col key={c.field} style={{ width: "8%" }} />
          ))}
        </colgroup>
        <thead>
          <tr>
            <th>Child Name</th>
            <th>CSPPID</th>
            <th>Enrollment Start Date</th>
            <th>Enrollment End Date</th>
            {EDITABLE_COLUMNS.map((c) => (
              <th key={c.field}>{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const errors = rowErrors[row.CSPPID] ?? {};
            return (
              <tr key={row.CSPPID}>
                <td>{row.ChildName}</td>
                <td>{row.CSPPID}</td>
                <td>{formatDateOnly(row.EnrollmentStartDate)}</td>
                <td>
                  {row.EnrollmentEndDate
                    ? formatDateOnly(row.EnrollmentEndDate)
                    : "—"}
                </td>
                {EDITABLE_COLUMNS.map((c) => {
                  const error = errors[c.field];
                  const errorId = `bulkAttn-${row.CSPPID}-${c.field}-error`;
                  const input = (
                    <TextInput
                      id={`bulkAttn-${row.CSPPID}-${c.field}`}
                      type="number"
                      value={row[c.field]}
                      disabled={disabled}
                      className={error ? "is-invalid" : undefined}
                      aria-label={`${c.label}, ${row.ChildName}`}
                      aria-required={c.isRequired || undefined}
                      aria-invalid={error ? true : undefined}
                      aria-describedby={error ? errorId : undefined}
                      onChange={(e) =>
                        onCellChange(row.CSPPID, c.field, e.target.value)
                      }
                    />
                  );
                  return (
                    <td key={c.field}>
                      {error ? (
                        <TooltipTrigger
                          tooltipText={error}
                          trigger={["hover", "focus"]}
                        >
                          {input}
                        </TooltipTrigger>
                      ) : (
                        input
                      )}
                      {error && (
                        <span role="alert" className="visually-hidden">
                          {`${c.label}, ${row.ChildName}: `}
                          <span id={errorId}>{error}</span>
                        </span>
                      )}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
